<template>
  <div>
    <sui-form warning>
      <sui-message warning icon="warning sign" v-if="isAvailable">
        <router-link class="ui green right floated button" to="/settings/plans" data-goal="SERVICE_PAGE_WEBHOOKS_SUBSCRIBE">Subscribe</router-link>
        Webhooks<br>is not available on your tariff plan!<br>
      </sui-message>
      <sui-segment emphasis="secondary">
        <sui-form-field required>
          <label>Actions</label>
          <sui-form-fields class="inline">
            <sui-form-field v-for="action in actions" :key="action.value">
              <sui-checkbox v-model="action.checked" :value="action.value" :label="action.text" :disabled="action.disabled" data-inverted :data-tooltip="action.tip"/>
            </sui-form-field>
          </sui-form-fields>
        </sui-form-field>
        <sui-form-fields>
          <sui-form-field :error="hasError" :width="16">
            <label>Link</label>
            <div class="ui small action input">
              <input type="text" v-model="link" readonly onclick="this.select()">
              <sui-button icon="copy" data-inverted :data-tooltip="linkTooltip" @click="copyLink($event)" data-goal="SERVICE_PAGE_WEBHOOKS_COPY" />
            </div>
          </sui-form-field>
          <sui-form-field>
            <label style="opacity: 0;">.</label>
            <sui-button basic class="link" color="red" icon="trash" :disabled="!actionsById[service.id].serviceUpdate" data-inverted data-tooltip="Generate new link" @click.prevent="regenerateLink()" data-goal="SERVICE_PAGE_WEBHOOKS_REGENERATE" />
          </sui-form-field>
        </sui-form-fields>
      </sui-segment>
    </sui-form>
    <br>
    <p>
      <b>What is it?</b><br>
      Webhooks allow D2C to take automatic actions when external events happen (push, build, release, etc).<br>
      When someone sends a request using this link, the system executes selected actions.
    </p>
    <p>
      <b>How to use webhooks?</b><br>
      <a href="https://docs.d2c.io/platform/webhooks/" target="_blank">D2C Docs</a>,
      <a href="https://developer.github.com/webhooks/creating/" target="_blank">GitHub webhooks</a>,
      <a href="https://docs.gitlab.com/ee/user/project/integrations/webhooks.html#overview" target="_blank">GitLab webhooks</a>,
      <a href="https://confluence.atlassian.com/bitbucket/manage-webhooks-735643732.html#Managewebhooks-create_webhookCreatingwebhooks" target="_blank">Bitbucket webhooks</a>
    </p>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'

  export default {
    name: 'Webhooks',
    props: {
      service: {
        type: Object,
        required: true,
      },
    },
    data() {
      const service = this.service
      return {
        actions: [
          {
            value: 'restart',
            text: 'Restart service',
            tip: null,
            checked: false,
            disabled: false,
          },
          {
            value: 'updateSources',
            text: 'Update sources',
            tip: 'Updating sources without deleting old files\nand without updating local dependencies',
            checked: service.type === 'python' || service.type === 'ruby',
            disabled: false,
          },
          {
            value: 'updateLocalDeps',
            text: 'Update local dependencies',
            tip: 'Updating sources and local dependencies, and preparing code to work\nExamples: gulp build, npm install, composer install, etc',
            checked: service.isApp && service.type !== 'python' && service.type !== 'ruby',
            disabled: false,
          },
          {
            value: 'updateGlobalDeps',
            text: 'Update global dependencies',
            tip: 'Updating global dependencies\nExamples: apt-get install, pip install, etc',
            checked: service.type === 'python' || service.type === 'ruby',
            disabled: false,
          },
          {
            value: 'updateVersion',
            text: 'Update service version',
            tip: `Updating global dependencies and the latest minor version or patch of service`,
            checked: service && !service.isApp,
            disabled: false,
          },
        ],
        error: null,
        key: null,
        keyLoading: false,
        linkTooltip: 'Copy',
      }
    },
    computed: {
      ...mapGetters(['actionsById', 'account', 'config']),
      isAvailable() {
        return !this.account.limits.hooks
      },
      hasError() {
        return Boolean(this.error)
      },
      link() {
        if (this.error) {
          return this.error
        }
        if (this.isAvailable) {
          return `Not available for your tariff plan`
        }
        if (!this.service) {
          return `Service is not selected`
        }
        if (this.keyLoading) {
          return `Loading...`
        }
        const actions = this.actions.filter(action => action.checked).map(action => action.value).join(',')
        return `${this.config.apiAddress}/hook/service/${this.key}?actions=${actions}`
      },
    },
    methods: {
      copyLink() {
        if (this.link) {
          event.preventDefault()
          this.$copyText(this.link).then(() => {
            this.linkTooltip = 'Copied!'
          }, () => {
            this.linkTooltip = 'Can\'t copy!'
            this.link = ''
          })
          setTimeout(() => {
            this.linkTooltip = 'Copy'
          }, 1000)
        }
      },
      updateLink(regenerate = false) {
        this.error = null
        this.keyLoading = true
        if (this.service) {
          this.service.getWebhook(regenerate).then(key => {
            this.key = key
          }).catch(error => {
            this.error = error
          }).finally(() => {
            this.keyLoading = false
          })
        }
      },
      regenerateLink() {
        return this.$modals.confirm.open({
          text: 'It will generate a new key and refresh the webhook link. The old one will not work!',
          callback: () => {
            this.updateLink(true)
          },
        })
      },
    },
    mounted() {
      if (!this.isAvailable) {
        this.updateLink()
      }
    },
  }
</script>

<style scoped>
  .input >>> input[readonly] { opacity: 1!important; }
</style>
